import { Form, Formik, type FormikProps } from 'formik';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { buildRequestSource } from 'utils';
import { formatDashedYYYYMMDD, getTripLengthInDays, parseDate } from 'utils/DateFormats';
import * as Yup from 'yup';
import { TRPC_SETTINGS } from '~/constants/trpc';
import useJourneyData from '~/hooks/useJourneyData';
import { getPathname } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { useJourneyActions } from '~/stores/journeyStore';
import { getFirstLegOriginId } from '~/utils/blueprintHelpers/isUSTrip';
import { toCustomerInfoMessages, writeOriginDateShiftNotice } from '~/utils/originDateShiftNotice';
import { minNumberRooms } from '~/utils/paxHelpers';
import type { JourneyEditFormValues } from './JourneyEditForm.types';

type TemplateEditFormProps = {
  children: (props: FormikProps<JourneyEditFormValues>) => ReactNode;
  isVirtualBlueprint?: boolean;
};

type TitleOverridable = {
  journeyTitleOverride?: string;
  buildParameters?: { journeyTitleOverride?: string };
};

function applyTitleOverride(user: TitleOverridable, title: string | null, isTemplateLikeJourney: boolean) {
  if (title) user.journeyTitleOverride = title;
  if (isTemplateLikeJourney && user.buildParameters?.journeyTitleOverride)
    user.journeyTitleOverride = user.buildParameters.journeyTitleOverride;
}

function persistFormValues(tripId: string, values: JourneyEditFormValues) {
  try {
    globalThis.sessionStorage.setItem(
      'journeyFormValues',
      JSON.stringify({
        journeyId: tripId,
        values: {
          adult_count: values.adult_count,
          child_count: values.child_count,
          childAges: values.childAges,
          room_count: values.room_count,
          closest_city: values.closest_city,
          duration: values.duration,
          startDate: values.startDate ? formatDashedYYYYMMDD(values.startDate) : null,
          endDate: values.endDate ? formatDashedYYYYMMDD(values.endDate) : null,
          twinRoomPreferred: values.twinRoomPreferred
        }
      })
    );
  } catch {}
}

function buildTripPageParams(source: string | null) {
  const params = new URLSearchParams();

  new URLSearchParams(globalThis.location.search).forEach((value, key) => {
    if (key !== 'blueprintId') {
      params.set(key, value);
    }
  });

  if (source) {
    params.set('source', source);
  }
  params.set('action', 'edit');

  return params;
}

function navigateToTripPage(href: string, onNavigationFailure: () => void) {
  try {
    globalThis.location.href = href;
  } catch (_error) {
    onNavigationFailure();
    setTimeout(() => {
      globalThis.location.href = href;
    }, 0);
  }
}

const TemplateEditForm = ({ children, isVirtualBlueprint = false }: TemplateEditFormProps) => {
  const { details, featuredActivities, items, region, source, user, uniqueId: previousId } = useJourneyData();

  // Even though we are using the returned data on a trip to display data about dates etc. outside, we still need the ref in store to check if the form is touched, so that we can determine if we are showing defaults or user-selected trip dates etc.
  const { setJourneyEditErrors, setFormRef, setIsCheckingAvailability } = useJourneyActions();

  const formRef = useRef<FormikProps<JourneyEditFormValues> | null>(null);
  const hasInitializedWithStoredValues = useRef(false);
  const lockedInitialValues = useRef<JourneyEditFormValues | null>(null);

  const { data: origins } = api.cms.origins.useQuery({ tripId: previousId }, { ...TRPC_SETTINGS.QUERY_NO_REFETCH });

  const firstLegOriginId = getFirstLegOriginId(items);

  const locale = useLocale();
  const t = useTranslations('journey');
  const searchParams = useSearchParams();
  const title = searchParams.get('title');
  const { creationSource, previousIds } = source;
  const { startDate, endDate, hasPinnedAccommodation } = details;
  const duration = getTripLengthInDays(startDate, endDate);
  const startDateAsDate = parseDate(startDate);
  const endDateAsDate = parseDate(endDate);

  const {
    twinRoomPreferred
    // isOneWayTrip, missing - will marketing produce one way trips?
  } = user;

  const adultCount = user.numberOfAdults || user.adult_count;
  const roomCount = user.numberOfRooms || user.room_count;

  const closest_city = (() => {
    const firstItem = items[0];
    if (firstItem?.type === 'Journey') {
      return firstItem.origin;
    }

    return items.find((item) => item.type === 'Journey')?.origin || 'Unknown';
  })();

  const featuredActivityIds = featuredActivities?.map((activity) => activity.id);

  const [isTouched, setIsTouched] = useState(false);

  const isBlueprintJourney = creationSource === 'FROM_TEMPLATE';
  const isVirtualBlueprintJourney = creationSource === 'FROM_VIRTUAL_BLUEPRINT_TEMPLATE' || isVirtualBlueprint;
  const isPlaygroundJourney = creationSource.includes('PLAYGROUND');
  const isTemplateLikeJourney = isBlueprintJourney || isPlaygroundJourney || isVirtualBlueprintJourney;

  const storedFormValues = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = sessionStorage.getItem('journeyFormValues');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.journeyId === previousId) {
          return parsed.values;
        }
      }
    } catch (_e) {
      // Ignore errors
    }
    return null;
  }, [previousId]);

  const initialValues = useMemo<JourneyEditFormValues>(() => {
    if (lockedInitialValues.current) {
      return lockedInitialValues.current;
    }

    if (storedFormValues) {
      const values = {
        adult_count: storedFormValues.adult_count ?? adultCount ?? 2,
        child_count: storedFormValues.child_count ?? user.childAges.length ?? 0,
        childAges:
          storedFormValues.childAges ?? (isBlueprintJourney || isVirtualBlueprintJourney ? user.childAges : []),
        room_count: storedFormValues.room_count ?? roomCount ?? 1,
        closest_city: storedFormValues.closest_city ?? closest_city,
        duration: storedFormValues.duration ?? duration,
        startDate: storedFormValues.startDate
          ? parseDate(storedFormValues.startDate)
          : isBlueprintJourney || isPlaygroundJourney || isVirtualBlueprintJourney
            ? startDateAsDate
            : null,
        endDate: storedFormValues.endDate
          ? parseDate(storedFormValues.endDate)
          : isBlueprintJourney || isPlaygroundJourney || isVirtualBlueprintJourney
            ? endDateAsDate
            : null,
        twinRoomPreferred: storedFormValues.twinRoomPreferred ?? twinRoomPreferred
      };
      lockedInitialValues.current = values;
      return values;
    }

    return {
      adult_count: adultCount || 2,
      child_count: user.childAges.length || 0,
      childAges: isBlueprintJourney || isVirtualBlueprintJourney ? user.childAges : [],
      room_count: roomCount || 1,
      closest_city,
      duration,
      startDate: isBlueprintJourney || isPlaygroundJourney || isVirtualBlueprintJourney ? startDateAsDate : null,
      endDate: isBlueprintJourney || isPlaygroundJourney || isVirtualBlueprintJourney ? endDateAsDate : null,
      twinRoomPreferred
    };
  }, [
    storedFormValues,
    adultCount,
    user.childAges,
    isBlueprintJourney,
    isVirtualBlueprintJourney,
    roomCount,
    closest_city,
    duration,
    isPlaygroundJourney,
    startDateAsDate,
    endDateAsDate,
    twinRoomPreferred
  ]);

  useEffect(() => {
    if (formRef?.current) setFormRef(formRef.current);
  }, [formRef, setFormRef, formRef?.current?.dirty]);

  useEffect(() => {
    if (storedFormValues && !hasInitializedWithStoredValues.current) {
      hasInitializedWithStoredValues.current = true;
      const timer = setTimeout(() => {
        try {
          sessionStorage.removeItem('journeyFormValues');
        } catch (_e) {
          // Ignore errors
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (!storedFormValues && lockedInitialValues.current) {
      lockedInitialValues.current = null;
      hasInitializedWithStoredValues.current = false;
    }
  }, [storedFormValues]);

  const buildRequestBody = (
    values: JourneyEditFormValues,
    childAgesAsNumbers: JourneyEditFormValues['childAges'],
    originId: number | undefined
  ) => ({
    ...values,
    childAges: childAgesAsNumbers,
    ...(featuredActivities && {
      featuredActivities: featuredActivityIds
    }),
    startDate: values.startDate ? formatDashedYYYYMMDD(values.startDate) : '',
    endDate: values.endDate ? formatDashedYYYYMMDD(values.endDate) : '',
    journeyId: previousId,
    originLocationId: Number(originId),
    region: region.title,
    buildParameters: {
      ...user,
      closest_city: originId,
      originLocationId: Number(originId)
    },
    previousIds,
    closest_city: originId,
    requestSource: buildRequestSource({
      environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
      source: searchParams.get('source'),
      lp: searchParams.get('lp'),
      previous: user?.requestSource,
      action: isTemplateLikeJourney ? 'edit' : 'create'
    }),
    useYearRoundPricingCache: false
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={!hasInitializedWithStoredValues.current}
      innerRef={formRef}
      validateOnBlur={isTouched}
      validateOnChange={isTouched}
      validationSchema={Yup.object().shape({
        adult_count: Yup.number()
          .min(1, t('validation_adults_required'))
          .max(6, t('validation_adults_max'))
          .required(t('validation_adults_required')),
        child_count: Yup.number().max(6, t('validation_children_max')),
        childAges: Yup.array()
          .of(Yup.number().typeError(t('validation_child_ages')))
          .when(['child_count'], ([child_count], schema) => {
            if (child_count > 0) {
              return schema
                .of(Yup.string().required(t('validation_child_ages')))
                .min(child_count, t('validation_child_ages'));
            }
            return schema;
          }),
        room_count: Yup.number()
          .max(6, t('validation_rooms_max'))
          .required(t('validation_rooms_required'))
          .when(['adult_count', 'childAges'], ([adult_count, childAges], schema) => {
            const minRooms = minNumberRooms(adult_count, childAges);
            return schema.min(minRooms, t('validation_rooms_min', { count: minRooms }));
          }),
        closest_city: Yup.string().required(t('validation_closest_city')),
        startDate: Yup.date()
          .min(new Date(), t('validation_start_date_future'))
          .nullable()
          .required(t('validation_start_date_required'))
      })}
      onSubmit={async (values, { setSubmitting }) => {
        setIsCheckingAvailability(true);
        applyTitleOverride(user, title, isTemplateLikeJourney);

        values.childAges = values.childAges.slice(0, values.child_count);
        const childAgesAsNumbers = values.childAges.filter((age) => !Number.isNaN(age));

        const originId =
          (origins ?? []).find((origin) => origin.locationName === values.closest_city)?.id ?? firstLegOriginId;

        if (hasPinnedAccommodation) {
          dataLayer.push({
            event: 'date_edit',
            pinnedAccommodation: hasPinnedAccommodation
          });
        }

        const response = await fetch('/api/template-journey', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildRequestBody(values, childAgesAsNumbers, originId))
        });

        if (response.status !== 200) {
          setJourneyEditErrors(['UNKNOWN_FAILURE']);
          setSubmitting(false);
          setIsCheckingAvailability(false);
          return;
        }

        const responseData = await response.json();
        const { failureMessages, uniqueId: tripId, customerInfoMessages } = responseData.data;

        if (failureMessages.length > 0) {
          setJourneyEditErrors(failureMessages);
          return;
        }

        setJourneyEditErrors([]);

        const originDateShiftMessages = toCustomerInfoMessages(customerInfoMessages);
        if (originDateShiftMessages.length > 0) {
          // The backend moved the dates, so let the trip page read them from the new journey instead.
          persistFormValues(tripId, { ...values, startDate: null, endDate: null });
          writeOriginDateShiftNotice(tripId, originDateShiftMessages);
        } else {
          persistFormValues(tripId, values);
        }

        const journeyPath = `/journey/${tripId}`;
        const href = `${getPathname({ href: journeyPath, locale })}?${buildTripPageParams(searchParams.get('source')).toString()}`;

        navigateToTripPage(href, () => setIsCheckingAvailability(false));
      }}
    >
      {(props) => (
        <Form>
          {children({
            ...props,
            handleSubmit() {
              setIsTouched(true);
              props.handleSubmit();
            }
          })}
        </Form>
      )}
    </Formik>
  );
};

export { TemplateEditForm };
